(() => {
  if (window.NavelisColours) return;
  const records = new Map(), queue = [];
  const loadedScripts = new Set([...document.scripts].map(script => script.src));
  const ttl = 60000, maxEntries = 24, maxBytes = 4000000;
  let running = 0, revision = 0, restore = null, timer, generation = 0;
  const allowed = () => window.NavelisProductResponses?.allowed();
  const current = () => document.querySelector('#MainContent [data-product-section]');
  const normalize = value => {
    const url = new URL(value, location.origin);
    if (url.origin !== location.origin || !url.pathname.includes('/products/')) throw new Error('Invalid colour URL');
    url.hash = ''; url.searchParams.sort(); return url.href;
  };
  const targetUrl = (link, section = current()) => {
    const url = new URL(link.href);
    const names = JSON.parse(link.dataset.colourOptionNames || '[]');
    const variants = JSON.parse(link.dataset.colourVariants || '[]');
    const selected = [...section.querySelectorAll('[data-product-option]')].map(option => {
      const control = option.querySelector('input:checked, select');
      const value = control?.selectedOptions?.[0] || control;
      return [option.dataset.optionName, value?.dataset.optionValueName];
    }).filter(([name, value]) => name && value && names.includes(name));
    const match = selected.length && variants.find(variant => selected.every(([name, value]) => variant.options[names.indexOf(name)] === value));
    if (match) url.searchParams.set('variant', match.id);
    return normalize(url.href);
  };
  const trim = () => {
    let bytes = [...records.values()].reduce((n, r) => n + (r.data?.html.length || 0) * 2, 0);
    for (const [key, record] of records) {
      if (records.size <= maxEntries && bytes <= maxBytes) break;
      if (!record.data) continue;
      bytes -= record.data.html.length * 2; records.delete(key);
    }
  };
  const parse = html => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const main = doc.querySelector('#MainContent');
    if (!main?.querySelector('[data-product-section]') || main.querySelector('.shopify-app-block, [data-purchase-options], model-viewer')) throw new Error('Use full product navigation');
    // A different template requiring new JS must initialize through a real page load.
    for (const script of document.scripts) loadedScripts.add(script.src);
    for (const script of main.querySelectorAll('script')) {
      if (script.type === 'application/ld+json' || script.type === 'application/json') continue;
      if (!script.src || !loadedScripts.has(script.src)) throw new Error('Use full product navigation');
      script.remove();
    }
    return { html: main.innerHTML, title: doc.title, handle: doc.body.dataset.productHandle, id: doc.body.dataset.productId,
      meta: [...doc.head.querySelectorAll('link[rel="canonical"], meta[name="description"], meta[property^="og:"], meta[name^="twitter:"], script[data-product-structured-data]')].map(node => node.outerHTML),
      image: main.querySelector('[data-media-gallery] img')?.outerHTML };
  };
  const warmImage = data => {
    if (!allowed() || !data.image) return;
    const node = new DOMParser().parseFromString(data.image, 'text/html').querySelector('img');
    const image = new Image(); image.fetchPriority = 'low'; image.decoding = 'async';
    image.sizes = node.sizes; image.srcset = node.srcset; image.src = node.src;
    data.warmedImage = image;
  };
  const pump = () => {
    while (running < 2 && queue.length) {
      const job = queue.shift();
      if (job.generation !== generation || (!job.foreground && !allowed())) { records.delete(job.key); job.reject(new Error('Prefetch cancelled')); continue; }
      running++;
      fetch(job.key, { headers: { Accept: 'text/html' }, priority: job.foreground ? 'high' : 'low', signal: job.controller.signal })
        .then(response => { if (!response.ok) throw new Error('Product unavailable'); return response.text(); })
        .then(parse).then(data => {
          if (job.generation !== generation) throw new Error('Stale product response');
          job.data = data; job.expires = Date.now() + ttl; warmImage(data); trim(); job.resolve(data);
        }).catch(error => { if (records.get(job.key) === job) records.delete(job.key); job.reject(error); })
        .finally(() => { running--; pump(); });
    }
  };
  const read = (value, foreground = false) => {
    const key = normalize(value); let record = records.get(key);
    if (record?.data && record.expires <= Date.now()) { records.delete(key); record = null; }
    if (record) {
      if (foreground) { record.foreground = true; const index = queue.indexOf(record); if (index >= 0) { queue.splice(index, 1); queue.unshift(record); } }
      return record.promise;
    }
    const job = { key, foreground, generation, controller: new AbortController() };
    job.promise = new Promise((resolve, reject) => Object.assign(job, { resolve, reject }));
    records.set(key, job); foreground ? queue.unshift(job) : queue.push(job); pump(); return job.promise;
  };
  const cancel = () => { revision++; restore?.(); restore = null; };
  const clear = () => {
    generation++; cancel();
    for (const record of records.values()) record.controller.abort();
    for (const job of queue.splice(0)) job.reject(new Error('Prefetch cancelled'));
    records.clear();
  };
  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      const section = current();
      if (document.readyState !== 'complete' || !section || !allowed() || section.getAttribute('aria-busy') === 'true' || section.querySelector('[data-purchase-options]')) return;
      const links = [...section.querySelectorAll('[data-colour-product]:not([data-quick-view-trigger])')];
      // Only this model's linked colours, once per selection, never the catalogue.
      for (const link of links.slice(0, 24)) {
        try { read(targetUrl(link), false).catch(() => {}); } catch {}
      }
    }, 800);
  };
  const navigate = async (url, { history = true } = {}) => {
    cancel(); const own = revision, section = current(), main = document.querySelector('#MainContent');
    if (!section || section.getAttribute('aria-busy') === 'true') { location.assign(url); return; }
    const regions = [...section.querySelectorAll('[data-product-form-container], [data-product-sticky-add]')].map(element => ({ element, inert: element.inert }));
    const status = section.querySelector('[data-variant-status]');
    section.setAttribute('aria-busy', 'true'); regions.forEach(({element}) => { element.inert = true; });
    if (status) status.textContent = section.dataset.updatingLabel;
    restore = () => { section.removeAttribute('aria-busy'); regions.forEach(({element,inert}) => { element.inert = inert; }); if (status) status.textContent = ''; };
    const key = normalize(url), source = records.get(key)?.data ? 'memory' : records.has(key) ? 'pending' : 'network', started = Date.now();
    try {
      const data = await read(key, true);
      if (own !== revision || !section.isConnected) return;
      const fresh = new DOMParser().parseFromString(data.html, 'text/html');
      const freshSection = fresh.querySelector('[data-product-section]');
      if (!freshSection) throw new Error('Product unavailable');
      const selected = freshSection.dataset.selectedVariantId;
      const display = new URL(key); if (selected) display.searchParams.set('variant', selected);
      if (history) {
        window.history.replaceState({ ...window.history.state, navelisColour: true }, '', location.href);
        window.history.pushState({ navelisColour: true }, '', display.href);
      }
      restore = null;
      main.replaceChildren(...fresh.body.childNodes);
      document.title = data.title; document.body.dataset.productHandle = data.handle; document.body.dataset.productId = data.id;
      document.head.querySelectorAll('link[rel="canonical"], meta[name="description"], meta[property^="og:"], meta[name^="twitter:"], script[data-product-structured-data]').forEach(node => node.remove());
      for (const markup of data.meta) document.head.append(new DOMParser().parseFromString(markup, 'text/html').head.firstElementChild);
      freshSection.dataset.colourUpdateMs = String(Date.now() - started); freshSection.dataset.colourResponse = source;
      main.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
      window.Shopify?.PaymentButton?.init?.();
      freshSection.querySelector('[data-colour-product][aria-current]')?.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('product:variant-change', { detail: { sectionId: freshSection.dataset.sectionId, variantId: selected, productId: data.id } }));
      schedule();
    } catch {
      if (own !== revision || !section.isConnected) return;
      cancel(); location.assign(key);
    }
  };
  const intent = event => {
    const link = event.target.closest?.('#MainContent [data-product-section] [data-colour-product]:not([data-quick-view-trigger])');
    if (link && allowed()) { try { read(targetUrl(link), true).catch(() => {}); } catch {} }
  };
  document.addEventListener('pointerover', intent); document.addEventListener('focusin', intent);
  document.addEventListener('click', event => {
    const link = event.target.closest?.('#MainContent [data-product-section] [data-colour-product]:not([data-quick-view-trigger])');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank' || window.Shopify?.designMode) return;
    if (current()?.querySelector('[data-purchase-options]')) return;
    try { const url = targetUrl(link); event.preventDefault(); if (!link.hasAttribute('aria-current')) navigate(url); } catch {}
  });
  window.addEventListener('popstate', event => { if (event.state?.navelisColour) navigate(location.href, { history: false }); });
  ['cart:updated', 'product:purchase-option-change'].forEach(name => document.addEventListener(name, clear));
  document.addEventListener('visibilitychange', () => { if (document.hidden) clear(); else schedule(); });
  window.addEventListener('load', schedule, { once: true });
  window.NavelisColours = { schedule, cancel };
})();
